/* eslint-disable global-require */
import config from '../config';
import { isIgnoredDatadogErrorMessage, isWarningDatadogMessage } from './datadogFilters';

const datadogSettings = config.datadog || {};
const isBrowser = typeof window !== 'undefined';
const DEFAULT_PROPAGATOR_TYPES = ['tracecontext', 'datadog'];
const RUM_TRACKING_CONSENT_GRANTED = 'granted';
const RUM_TRACKING_CONSENT_NOT_GRANTED = 'not-granted';

let rumSdk;
let logsSdk;
let initialized = false;
let warnOnce = false;
let rumTrackingConsent = RUM_TRACKING_CONSENT_NOT_GRANTED;

const shouldDropIgnoredMessage = (message) => isIgnoredDatadogErrorMessage(message);

const classifyMessageAsWarning = (message) => isWarningDatadogMessage(message);

const logsBeforeSend = (event) => {
  if (!event || typeof event !== 'object') {
    return true;
  }

  const message = event.message || event.error?.message || '';
  if (shouldDropIgnoredMessage(message)) {
    return false;
  }

  if (event.status === 'error' && classifyMessageAsWarning(message)) {
    event.status = 'warn';
  }

  return true;
};

const rumBeforeSend = (event) => {
  if (!event || typeof event !== 'object' || event.type !== 'error') {
    return true;
  }

  const message = event.error?.message || '';

  if (shouldDropIgnoredMessage(message)) {
    return false;
  }

  if (classifyMessageAsWarning(message)) {
    return false;
  }

  return true;
};

const getWindow = () => (typeof window !== 'undefined' ? window : undefined);

const normalizeTracingUrl = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  return trimmedValue.replace(/\/+$/, '');
};

const resolveAllowedTracingUrls = () => {
  const configuredUrls = Array.isArray(datadogSettings.allowedTracingUrls)
    ? datadogSettings.allowedTracingUrls
    : [];
  const runtimeOrigin = getWindow()?.location?.origin;
  const candidateUrls = runtimeOrigin ? [...configuredUrls, runtimeOrigin] : configuredUrls;

  const uniqueUrls = [...new Set(candidateUrls.map(normalizeTracingUrl).filter(Boolean))];

  return uniqueUrls.map((url) => ({
    match: url,
    propagatorTypes: DEFAULT_PROPAGATOR_TYPES,
  }));
};

const isDatadogRuntimeEnabled = () => Boolean(isBrowser && datadogSettings.enabled);

const loadSdks = () => {
  if (!rumSdk) {
    const { datadogRum } = require('@datadog/browser-rum');
    rumSdk = datadogRum;
  }
  if (!logsSdk) {
    const { datadogLogs } = require('@datadog/browser-logs');
    logsSdk = datadogLogs;
  }
};

const applyGlobalContext = () => {
  if (!initialized || !logsSdk || typeof logsSdk.addLoggerGlobalContext !== 'function') {
    return;
  }

  logsSdk.addLoggerGlobalContext('service', datadogSettings.service);
  logsSdk.addLoggerGlobalContext('env', datadogSettings.env);
};

export const initializeDatadogClient = () => {
  if (!isDatadogRuntimeEnabled()) {
    return { rum: null, logs: null };
  }

  if (initialized) {
    return { rum: rumSdk, logs: logsSdk };
  }

  try {
    loadSdks();
  } catch (error) {
    if (!warnOnce) {
      // eslint-disable-next-line no-console
      console.warn('Datadog browser SDK failed to load', error);
      warnOnce = true;
    }
    return { rum: null, logs: null };
  }

  if (!rumSdk || !logsSdk) {
    return { rum: null, logs: null };
  }

  const allowedTracingUrls = resolveAllowedTracingUrls();
  const rumInitOptions = {
    applicationId: datadogSettings.applicationId,
    clientToken: datadogSettings.clientToken,
    site: datadogSettings.site,
    service: datadogSettings.service,
    env: datadogSettings.env,
    version: datadogSettings.version,
    sessionSampleRate: datadogSettings.sessionSampleRate,
    sessionReplaySampleRate: datadogSettings.sessionReplaySampleRate,
    trackResources: datadogSettings.trackResources,
    trackLongTasks: datadogSettings.trackLongTasks,
    trackUserInteractions: datadogSettings.trackUserInteractions,
    trackingConsent: rumTrackingConsent,
    beforeSend: rumBeforeSend,
    traceSampleRate: datadogSettings.traceSampleRate,
    traceContextInjection: datadogSettings.traceContextInjection,
  };

  if (allowedTracingUrls.length > 0) {
    rumInitOptions.allowedTracingUrls = allowedTracingUrls;
  }

  rumSdk.init(rumInitOptions);

  logsSdk.init({
    clientToken: datadogSettings.clientToken,
    site: datadogSettings.site,
    service: datadogSettings.service,
    env: datadogSettings.env,
    version: datadogSettings.version,
    forwardErrorsToLogs: datadogSettings.forwardErrorsToLogs,
    sampleRate: datadogSettings.logSampleRate,
    beforeSend: logsBeforeSend,
  });

  initialized = true;
  applyGlobalContext();

  return { rum: rumSdk, logs: logsSdk };
};

export const ensureDatadogClient = () => {
  if (!initialized) {
    return initializeDatadogClient();
  }
  return { rum: rumSdk, logs: logsSdk };
};

export const setDatadogRumEnabled = (enabled) => {
  rumTrackingConsent = enabled ? RUM_TRACKING_CONSENT_GRANTED : RUM_TRACKING_CONSENT_NOT_GRANTED;

  if (!isDatadogRuntimeEnabled()) {
    return;
  }

  const { rum } = ensureDatadogClient();
  if (rum && typeof rum.setTrackingConsent === 'function') {
    rum.setTrackingConsent(rumTrackingConsent);
  }
};

export const isDatadogEnabled = () => isDatadogRuntimeEnabled();

export const getDatadogSdks = () => ({ rum: rumSdk, logs: logsSdk });
